import {
  AfterViewChecked, AfterViewInit,
  Component, effect,
  ElementRef,
  inject,
  Input,
  OnInit,
  signal, untracked,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {DialogModule} from "primeng/dialog";
import {AvatarModule} from "primeng/avatar";
import {OverlayPanelModule} from 'primeng/overlaypanel';
import {ChatService} from "../../../../services/chat.service";
import {ChatMessage} from "../../../../shared/interfaces/chat";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {firstValueFrom, forkJoin, Observable} from "rxjs";
import {ChatMessageComponent} from "./chat-message/chat-message.component";
import {ChatInputComponent} from "./chat-input/chat-input.component";
import {NgClass, ViewportScroller} from "@angular/common";
import {ChatAIResponse, IChatHistory, KPIChartData, KPIChatResponseConsulta} from "../../../../shared/interfaces/database/chat-ia";
import { SpeedDialModule } from 'primeng/speeddial';
import {MenuItem} from "primeng/api";

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    DialogModule,
    AvatarModule,
    OverlayPanelModule,
    TranslatePipe,
    ChatMessageComponent,
    ChatInputComponent,
    NgClass,
    SpeedDialModule
  ],
  template: `
  <div class="dash-content">
    <div class="col-12 background-page gap-3 chat--container pr-0 pb-0 align-items-center">
      <p-speedDial className="setting-chat" buttonClassName="p-button-outlined
        bg-black-alpha-30 border-none w-3rem h-3rem text-white-alpha-90 shadow-4"
        showIcon="pi pi-cog" direction="down"
                   hideIcon="pi pi-times" [model]="menuItems" />
      @if (isEmptyMessage) {
        <div class="chat--header">
          <h1 class="font-30 text-white font-principal font-medium mb-0">
            {{ title() }}
          </h1>
          <h4 class="font-16 text-white font-principal mt-1 font-light">
            {{ 'components.app-chat.subtitle' | translate }}
          </h4>
          <p class="text-2xl text-white">{{ 'components.app-chat.subtitle-II' | translate }}</p>
        </div>
      } @else {
        <div class="chat--content overflow-y-auto scrollbar-thin-gray" #scrollContainer
             (scroll)="checkScrollPosition()">
          @for (message of messages(); track $index) {
            <app-chat-message class="w-full" [props]="message" [chartSource]="getChartSource"/>
          }
        </div>
      }
      @if (showScroll()) {
        <div class=" scroll-container">
          <button pButton icon="pi pi-arrow-down"
                  class="p-button-rounded p-button-icon-only  border-none
                hover:bg-black-alpha-90
               text-white shadow-4 bg-black-alpha-30"
                  (click)="scrollToBottom()"></button>
        </div>
      }

      <app-chat-input [isLoading]="loading" class="chat--input"
                      [ngClass]="{'chat-active':!isEmptyMessage}"
                      (value)="getPromptMessage($event)"/>
    </div>
  </div>
  `,
  styleUrl: './chat.component.scss'
})
export class ChatComponent implements OnInit {

  @Input({alias: 'title', required: true}) set _title(value: string) {
    this.title.set(value);
  }
  title = signal<string>('');
  loading: boolean = false;
  messages: WritableSignal<ChatMessage[]> = signal([]);
  translation: any = {};
  menuItems!: MenuItem[];
  requestMethod$!: (input: string) => Observable<any>;

  showScroll:WritableSignal<boolean> = signal(false);
  @ViewChild(ChatInputComponent, {static: true}) chatInput!: ChatInputComponent;
  @ViewChild("scrollContainer") scrollContainer!: ElementRef;

  //#region Services
  private readonly chatService: ChatService = inject(ChatService);
  private readonly _t: TranslateService = inject(TranslateService);
 private scroller: ViewportScroller = inject(ViewportScroller);
//#endregion Services

  getChartSource = (chart: KPIChatResponseConsulta): Observable<KPIChartData> =>
    this.chatService.getKPIChartData(chart.consultaGraficaId!);


  constructor() {
    this.initMenuItems();
  }

  ngOnInit(): void {
    this.getTranslations().then();
    this.getHistoryChat()
  }

  //#region Configuration

  scrollToBottom(): void {
    const element = this.scrollContainer.nativeElement;
    element.scrollTo({
      top: element.scrollHeight,
      behavior: 'smooth'
    });
    this.showScroll.set(false);
  }

   checkScrollPosition() {
    if (!this.scrollContainer)return;
    const element = this.scrollContainer.nativeElement;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;

      this.showScroll.set(!atBottom);

  }

  async getTranslations(): Promise<void> {
    this.translation = await firstValueFrom(forkJoin({
      subtitle: this._t.get('components.app-chat.subtitle'),
      text: this._t.get('components.app-chat.subtitle'),
    }))
  }

  private initMenuItems(){
    this.menuItems =[
      {
        icon: 'pi pi-trash',
        tooltipOptions: {
          tooltipLabel: 'Limpiar chat'
        },
        command :  () => this.deleteChat()
      }
    ]
  }
  //#endregion

  //#region actions in array messages

  private addMessage(prompt: string, time?:string): void {
    const item: ChatMessage = new ChatMessage(prompt, 'USER');
    if (time)item.dateTimeStr = time;
    this.messages.update(prev => [...prev, item]);
    this.chatInput.cleanInput()
  }

  private addResponseAI(response: ChatAIResponse): void {
    this.messages.update(prev => {
      const arrayPrev = [...prev];
      const lastObj = arrayPrev[arrayPrev.length - 1];
      if (!lastObj) return arrayPrev;
      lastObj.response = {
        header: response.header,
        data: response.data,
        tableMarkdown: response.tableMarkdown,
        bodyMarkdown: response.bodyMarkdown,
        consultas: response?.consultas,
      };
      return arrayPrev;
    })
  }

  private cleanChatMemory(): void {
    this.messages.set([]);
  }

  //#endregion actions in array messages

  //#region call API

  deleteChat(): void {
    this.chatService.cleanChatMemory()
      .subscribe(() => this.cleanChatMemory());
  }

  getHistoryChat(){
    this.chatService.getChatMemory().subscribe({
      next: (response: IChatHistory[]) => {
        this.restartChat(response);
      }
    })
  }

  //#endregion call API


  getPromptMessage(prompt:string){
    this.addMessage(prompt);
    this.loading = true;

    this.requestMethod$(prompt)
      .subscribe((response: ChatAIResponse) => {
        this.getResponseAI(response);
      });
  }

  getResponseAI(response: ChatAIResponse){
    this.loading = false;
    this.addResponseAI(response);
    setTimeout(() => this.scrollToBottom(), 1000);
  }



  restartChat(chats: IChatHistory[]): void {
    chats.forEach(chat => {
      if (chat.type == 'USER') {
        this.addMessage(chat.content, chat.timestamp);
      } else {
        const resp: ChatAIResponse = (chat.content.includes('{', 0)) ?
          JSON.parse(chat.content) : {bodyMarkdown: chat.content};
        this.addResponseAI(resp);
      }
    })
  }


  get isEmptyMessage(): boolean {
    return !(this.messages().length > 0);
  }


}
